/**
 * Modal overlay for creating a new DTA amendment (version). Supports optional file upload with EDC
 * domain discovery.
 */

import Alert from "@cloudscape-design/components/alert"
import Box from "@cloudscape-design/components/box"
import Button from "@cloudscape-design/components/button"
import FileUpload from "@cloudscape-design/components/file-upload"
import FormField from "@cloudscape-design/components/form-field"
import Input from "@cloudscape-design/components/input"
import Modal from "@cloudscape-design/components/modal"
import RadioGroup from "@cloudscape-design/components/radio-group"
import SpaceBetween from "@cloudscape-design/components/space-between"
import { useState } from "react"
import type { ParsedFormResult, UploadType } from "../../../services/dtaApi"
import {
  useUploadAndParseEdc,
  useUploadAndParseSdtm,
  useUploadDocument,
} from "../../../hooks/queries/useDtas"
import { selectReuploadForm } from "./reuploadForms"

interface AmendModalProps {
  /**
   * Whether the modal is visible.
   */
  open: boolean
  /**
   * Callback to close the modal.
   */
  onClose: () => void
  /**
   * Current value of the amendment reason input.
   */
  reason: string
  /**
   * Callback when the reason text changes.
   */
  onReasonChange: (value: string) => void
  /**
   * Callback to submit the amendment.
   */
  onSubmit: () => void
  /**
   * Whether the amend request is in flight.
   */
  loading: boolean
  /**
   * Error from the last failed amend request, shown inline.
   */
  error?: string | null
  /**
   * Study ID for presigning uploads.
   */
  studyId?: string
  /**
   * Source type of the DTA (edc or third-party).
   */
  sourceType?: string
  /**
   * Current domain of the DTA, used for pre-selection in EDC domain list.
   */
  currentDomain?: string
  /**
   * Callback when a file is uploaded and upload_id is obtained.
   */
  onUploadIdChange?: (uploadId: string | undefined) => void
}

/**
 * Renders a modal dialog for creating a new DTA version with a change reason. Optionally allows
 * uploading a source document to trigger extraction on the new version.
 */
const AmendModal = ({
  open,
  onClose,
  reason,
  onReasonChange,
  onSubmit,
  loading,
  error,
  studyId,
  sourceType,
  currentDomain,
  onUploadIdChange,
}: AmendModalProps) => {
  const [file, setFile] = useState<File | null>(null)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [uploadId, setUploadId] = useState<string | null>(null)
  const [edcForms, setEdcForms] = useState<ParsedFormResult[] | null>(null)
  const [selectedDomain, setSelectedDomain] = useState<string | null>(null)

  const uploadDocument = useUploadDocument(studyId ?? "")
  const uploadAndParseEdc = useUploadAndParseEdc(studyId ?? "")
  const uploadAndParseSdtm = useUploadAndParseSdtm(studyId ?? "")
  const uploading =
    uploadDocument.isPending || uploadAndParseEdc.isPending || uploadAndParseSdtm.isPending
  const hasSelectedFileWithoutUpload = file !== null && uploadId === null

  const handleFileSelect = async (selectedFile: File) => {
    setFile(selectedFile)
    setUploadError(null)
    setUploadId(null)
    onUploadIdChange?.(undefined)
    setEdcForms(null)
    setSelectedDomain(null)

    if (!studyId) {
      setUploadError("Study ID not available")
      return
    }

    try {
      // Spec-derived sources (EDC/SDTM) upload + parse to discover domains; otherwise just upload.
      if (sourceType === "edc" || sourceType === "sdtm") {
        const parseResult =
          sourceType === "sdtm"
            ? await uploadAndParseSdtm.mutateAsync({ file: selectedFile })
            : await uploadAndParseEdc.mutateAsync({ file: selectedFile })
        setUploadId(parseResult.upload_id)
        onUploadIdChange?.(parseResult.upload_id)
        setEdcForms(parseResult.forms)
        // Pre-select the form matching the current DTA domain (shared rule).
        const preselected = selectReuploadForm(parseResult.forms, currentDomain)
        if (preselected) setSelectedDomain(preselected)
      } else {
        const uploadType: UploadType = "dta"
        const result = await uploadDocument.mutateAsync({ file: selectedFile, uploadType })
        setUploadId(result.upload_id)
        onUploadIdChange?.(result.upload_id)
      }
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : "Upload failed")
      setUploadId(null)
      onUploadIdChange?.(undefined)
    }
  }

  const handleRemoveFile = () => {
    setFile(null)
    setUploadId(null)
    setEdcForms(null)
    setSelectedDomain(null)
    setUploadError(null)
    onUploadIdChange?.(undefined)
  }

  const handleClose = () => {
    handleRemoveFile()
    onClose()
  }

  return (
    <Modal
      visible={open}
      onDismiss={handleClose}
      size="medium"
      header="Amend DTA"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={onSubmit}
              disabled={!reason || loading || uploading || hasSelectedFileWithoutUpload}
              loading={loading}
            >
              {loading ? "Creating..." : "Create Amendment"}
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        {error && (
          <Alert type="error" header="Amendment failed">
            {error}
          </Alert>
        )}

        <Box variant="p">
          Create a new version of this DTA. The current content will be preserved as a snapshot.
        </Box>

        <FormField label="Reason for amendment">
          <Input
            value={reason}
            onChange={({ detail }) => onReasonChange(detail.value)}
            placeholder="e.g., Protocol Amendment 3"
            autoFocus
          />
        </FormField>

        <FormField label="Upload source document (optional)">
          <FileUpload
            value={file ? [file] : []}
            onChange={({ detail }) => {
              const selected = detail.value[0]
              if (selected) {
                void handleFileSelect(selected)
              } else {
                handleRemoveFile()
              }
            }}
            i18nStrings={{
              uploadButtonText: () => "Choose file",
              dropzoneText: () => "Click to select a file or drag and drop",
              removeFileAriaLabel: () => "Remove",
              limitShowFewer: "Show fewer",
              limitShowMore: "Show more",
              errorIconAriaLabel: "Error",
            }}
            accept=".xlsx,.xls,.csv,.pdf,.docx"
            showFileSize
            showFileLastModified={false}
            constraintText={uploading ? "Uploading..." : undefined}
          />
        </FormField>

        {uploadError && <Alert type="error">{uploadError}</Alert>}

        {file && uploadId && (
          <Alert type="warning">
            Parsed data will overwrite existing content in the new version. Previous version content
            will not be carried over.
          </Alert>
        )}

        {/* EDC domain selection */}
        {edcForms && edcForms.length > 0 && (
          <FormField label="Select domain from parsed file:">
            <RadioGroup
              value={selectedDomain}
              onChange={({ detail }) => setSelectedDomain(detail.value)}
              items={edcForms.map((form) => ({
                value: form.name,
                label: `${form.label || form.name} (${form.field_count} fields, ${form.codelist_count} codelists)`,
              }))}
            />
          </FormField>
        )}
      </SpaceBetween>
    </Modal>
  )
}

export default AmendModal
